import { PagedData } from "@/services/models/shared/PagedData";
import { AxiosResponse } from "axios";
import {
  FiscalYear,
  PayPeriodFilterOptions,
  PayPeriod,
  TTIAndDepartmentAccountsFilterOptions,
  TTIAndDepartmentAccounts,
  AdLocFilterOptions,
  AdLoc,
  ResponsiblePeopleFilterOptions,
  ResponsiblePerson,
  AccountPayeesFilterOptions,
  Account,
  UpdateAdLoc,
  UpdateResponsiblePerson,
  UpdatePayee,
  UpdateTTIAndDepartmentAccounts,
  AICAttachments,
  UpdatePayPeriod
} from "@/services/models/AcademicIndirectCost";
import FileProvider from "@/utilities/providers/FileProvider";
import BimsHttpService from "@/services/BimsHttpService";

class AcademicIndirectCostService {
  /**
   * @description Returns a list of fiscal years
   */
  getFiscalYears(): Promise<Array<FiscalYear>> {
    return BimsHttpService.get(`/AcademicIndirectCost/FiscalYears`).then((response: AxiosResponse<Array<FiscalYear>>) => {
      return response.data as Array<FiscalYear>;
    });
  }

  /**
   * @description Returns a paged list of pay periods
   */
  getPayPeriods(filter: PayPeriodFilterOptions): Promise<PagedData<PayPeriod>> {
    return BimsHttpService.get(`/AcademicIndirectCost/PayPeriods`, {
            params: filter
        }).then((response: AxiosResponse<PagedData<PayPeriod>>) => {
      return response.data as PagedData<PayPeriod>;
    });
  }

  /**
   * @description Returns a pay period
   */
  getPayPeriod(payPeriodId: string): Promise<PayPeriod> {
    return BimsHttpService.get(`/AcademicIndirectCost/PayPeriods/${payPeriodId}`).then((response: AxiosResponse<PayPeriod>) => {
      return response.data as PayPeriod;
    });
  }

  /**
   * @description Returns a list of adlocs
   */
  getAdLocs(filter: AdLocFilterOptions): Promise<Array<AdLoc>> {
    return BimsHttpService.get(`/AcademicIndirectCost/AdLocs`, {
            params: filter
        }).then((response: AxiosResponse<Array<AdLoc>>) => {
      return response.data as Array<AdLoc>;
    });
  }

  /**
   * @description Returns a list of tti and department accounts
   */
  getTTIAndDepartmentAccounts(filter: TTIAndDepartmentAccountsFilterOptions): Promise<Array<TTIAndDepartmentAccounts>> {
    return BimsHttpService.get(`/AcademicIndirectCost/TTIAndDepartmentAccounts`, {
            params: filter
        }).then((response: AxiosResponse<Array<TTIAndDepartmentAccounts>>) => {
      return response.data as Array<TTIAndDepartmentAccounts>;
    });
  }

  /**
   * @description Returns a list of responsible People
   */
  getResponsiblePeople(filter: ResponsiblePeopleFilterOptions): Promise<Array<ResponsiblePerson>> {
    return BimsHttpService.get(`/AcademicIndirectCost/ResponsiblePeople`, {
            params: filter
        }).then((response: AxiosResponse<Array<ResponsiblePerson>>) => {
      return response.data as Array<ResponsiblePerson>;
    });
  }

  /**
   * @description Returns a list of accounts with their payees
   */
  getAccountPayees(filter: AccountPayeesFilterOptions): Promise<Array<Account>> {
    return BimsHttpService.get(`/AcademicIndirectCost/Payees`, {
            params: filter
        }).then((response: AxiosResponse<Array<Account>>) => {
      return response.data as Array<Account>;
    });
  }

 /**
 * @description Updates the pay period information
 */
  updatePayPeriod(payPeriod: UpdatePayPeriod): Promise<any> {
    return BimsHttpService.put(`/AcademicIndirectCost/PayPeriods`, payPeriod);
  }

 /**
   * @description Updates the Adloc information
   */
  updateAdLoc(adLoc: UpdateAdLoc): Promise<any> {
    return BimsHttpService.put(`/AcademicIndirectCost/AdLocs`, adLoc);
  }

 /**
   * @description Updates the tti and department account information
   */
  updateTTIAndDepartmentAccounts(accounts: UpdateTTIAndDepartmentAccounts): Promise<any> {
    return BimsHttpService.put(`/AcademicIndirectCost/Accounts`, accounts);
  }

 /**
   * @description Updates the responsible person information
   */
  updateResponsiblePerson(responsiblePerson: UpdateResponsiblePerson): Promise<any> {
    return BimsHttpService.put(`/AcademicIndirectCost/ResponsiblePeople`, responsiblePerson);
  }

 /**
   * @description Updates the payee's information
   */
  updatePayee(payee: UpdatePayee): Promise<any> {
    return BimsHttpService.put(`/AcademicIndirectCost/Payees`, payee);
  }

  /**
   * @description Downloads the AIC account details report
   */
  downloadAICAccountDetailsReport(payPeriodStartDate: Date, fiscalYear: number): Promise<any> {
      return BimsHttpService.get(
          `/AcademicIndirectCost/Reports/AccountDetails/${fiscalYear}/${payPeriodStartDate}`, {
              responseType: 'blob'
          }).then(response => {
          return FileProvider.ForceFileDownload(response)
      })
  }

  /**
   * @description Downloads the AIC funds transfer report
   */
  downloadAICFundsTransferReport(payPeriodStartDate: Date, data: string): Promise<any> {
      return BimsHttpService.post(
          `/AcademicIndirectCost/Reports/FundsTransfer`, { data: data, payPeriodStartDate: payPeriodStartDate }, {
            responseType: 'blob'
          }).then(response => {
          return FileProvider.ForceFileDownload(response)
      })
  }

  /**
   *  @description Adds new attachment for the specified pay period id
   */
  addAttachment(payPeriodId: string, data: any): Promise<string> {
      return BimsHttpService.put(`/AcademicIndirectCost/${payPeriodId}/attachments`, data)
  } 

  /**
   * @description Returns a list of attachments.
   */
  getAttachmentList(payPeriodId: string): Promise<Array<AICAttachments>> {
      return BimsHttpService.get(`/AcademicIndirectCost/${payPeriodId}/Attachments`).then((response: AxiosResponse<Array<AICAttachments>>) => {
          return response.data as Array<AICAttachments>;
      });
  }

  /**
   *  @description Downloads the attachment with specified id
   */
  downloadAttachment (id: string): Promise<any> {
      return BimsHttpService.get(`/AcademicIndirectCost/Attachments/${id}`, { responseType: 'arraybuffer' })
          .then((response: AxiosResponse<any>) => {
              const url = window.URL.createObjectURL(new Blob([response.data]));
              const link = document.createElement("a");
              link.href = url;
      
              link.setAttribute("download", (response.headers['content-disposition'].split('filename=')[1].split(';')[0]).replace(/['"]+/g, ''));
              document.body.appendChild(link);
              link.click();
      
              // Remove the child after the doanload has completed
              document.body.removeChild(link);
          })
          .catch(error => {
            throw new TextDecoder('utf-8').decode(new Uint8Array(error.response.data)).replace(/['"]+/g, '')
          });
  }
}

export default new AcademicIndirectCostService();
